/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, NotFoundException } from '@nestjs/common';
import { TriggerClient } from '@/client/trigger-client';
import { ActionContext, ILogAttributes, TLogContent } from '@/client';

/**
 * 任务执行结果接口
 */
export interface ActionExecutionResult {
  trigger: string;
  success: boolean;
  result?: any;
  error?: string;
  startTime: Date;
  endTime: Date;
  duration: number;
}

/**
 * AutomationService
 * 
 * 提供自动化任务的执行、查询和管理功能
 */
@Injectable()
export class AutomationService {
  constructor(
    private readonly triggerClient: TriggerClient) { }

  /**
   * 执行指定的自动化任务
   * 
   * @param trigger - 任务触发器标识
   * @param instanceID - 任务实例ID
   * @param context - 任务执行上下文
   * @returns 任务执行报告数据
   */
  executeAction(trigger: string, instanceID: string, context: ActionContext): ActionContext {
    return this.triggerClient.executeAction(trigger, instanceID, context);
  }

  /**
   * 上报任务执行结果
   */
  report(msg: TLogContent, attributes: ILogAttributes) {
    this.triggerClient.report(msg, attributes);
  }

  /**
   * 获取指定任务的元数据信息
   * 
   * @param trigger - 任务触发器标识
   * @returns 任务元数据
   * @throws NotFoundException - 当任务不存在时抛出异常
   */
  getActionInfo(trigger: string) {
    const metadata = this.triggerClient.getActionMetadata(trigger);
    if (!metadata) {
      throw new NotFoundException(`Action not found: ${trigger}`);
    }
    return metadata;
  }

  /**
   * 检查指定任务是否存在
   * 
   * @param trigger - 任务触发器标识
   * @returns 存在返回 true，否则返回 false
   */
  hasAction(trigger: string): boolean {
    return this.triggerClient.getAction(trigger) !== undefined;
  }

  /**
   * 获取指定任务的处理器
   * 
   * @param trigger - 任务触发器标识
   * @returns 任务处理器
   */
  getAction(trigger: string) {
    return this.triggerClient.getAction(trigger);
  }

  /**
   * 获取所有已注册的任务
   * 
   * @returns 所有任务映射
   */
  getAllActions() {
    return this.triggerClient.getAllActions();
  }
}
